import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Box } from '@mui/material'
import {
  Text,
  type TextVariant,
  TextWithTooltip,
  Utils,
} from '@pbt/pbt-ui-components'

import { AutoshipInfo } from '~/components/common/AutoshipInfo'
import type { AutoshipFrequencyOption } from '~/constants/autoship'
import { useIsChewyCheckoutEnabled } from '~/store/hooks/business'
import { getAutoshipUnit } from '~/store/reducers/constants'
import type { Prescription } from '~/types'
import {
  getPrescriptionAutoshipFrequency,
  getPrescriptionAutoshipUnitId,
  getPrescriptionType,
  getRxType,
} from '~/utils/prescription'

import styles from './PrescriptionChewyOrderInfo.module.scss'

interface PrescriptionChewyOrderInfoProps {
  displayAsRow?: boolean
  prescription: Prescription
}

const PrescriptionChewyOrderInfo = ({
  prescription,
  displayAsRow,
}: PrescriptionChewyOrderInfoProps) => {
  const { t } = useTranslation('Dialogs')

  const AutoshipUnit = useSelector(getAutoshipUnit)

  const isChewyCheckoutEnabled = useIsChewyCheckoutEnabled()
  const { isVetDiet } = getRxType(prescription)
  const { isChewyActiveRx } = getPrescriptionType(
    prescription.prescriptionType,
    prescription.origin,
  )
  const isMissingOrderInfo = !prescription.orderNumber || !prescription.rxNo

  const autoshipUnitId = getPrescriptionAutoshipUnitId(prescription)
  const autoshipFrequency = getPrescriptionAutoshipFrequency(prescription)

  const isChewyCheckoutEnabledAndHasAutoship =
    isChewyCheckoutEnabled && autoshipUnitId && autoshipFrequency

  const getTooltipText = (
    children: ReactNode,
    variant: keyof typeof TextVariant,
  ) => (
    <TextWithTooltip
      allowWrap
      strong
      component="div"
      tooltipText={
        <>
          {t('Dialogs:PRESCRIPTION_DIALOG.CHEWY_ORDER_INFO_TOOLTIP')}
          <br />
          {t(
            'Dialogs:PRESCRIPTION_DIALOG.CHEWY_ORDER_INFO_TOOLTIP_PHONE_NUMBER',
          )}
        </>
      }
      variant={variant}
    >
      {children}
    </TextWithTooltip>
  )

  const getChewyOrderInfo = () => (
    <div className={displayAsRow ? styles.orderInfoRow : undefined}>
      <Text variant="body2">
        {t('Dialogs:PRESCRIPTION_DIALOG.CHEWY_ORDER_NUMBER')}:{' '}
        {prescription.orderNumber || '—'}
      </Text>
      <Text variant="body2">
        {isVetDiet
          ? t('Dialogs:PRESCRIPTION_DIALOG.CHEWY_RECIPE_NUMBER')
          : t('Dialogs:PRESCRIPTION_DIALOG.CHEWY_PRESCRIPTION_NUMBER')}
        {': '}
        {prescription.rxNo || '—'}
      </Text>
    </div>
  )

  const getMissingOrderInfo = () =>
    isChewyActiveRx &&
    isMissingOrderInfo && (
      <Text variant="body2">
        {t('Dialogs:PRESCRIPTION_DIALOG.CHEWY_ORDER_INFO_MISSING')}
      </Text>
    )

  return (
    <Box>
      {!displayAsRow ? (
        <>
          {getTooltipText(
            t('Dialogs:PRESCRIPTION_DIALOG.CHEWY_ORDER_INFO'),
            'subheading3',
          )}
          {getMissingOrderInfo()}
          {getChewyOrderInfo()}
        </>
      ) : (
        <>
          {getMissingOrderInfo()}
          {getTooltipText(getChewyOrderInfo(), 'body2')}
        </>
      )}
      {isChewyCheckoutEnabledAndHasAutoship && (
        <AutoshipInfo
          frequency={
            {
              uom: Utils.getConstantName(autoshipUnitId, AutoshipUnit),
              value: autoshipFrequency,
            } as AutoshipFrequencyOption
          }
        />
      )}
    </Box>
  )
}

export default PrescriptionChewyOrderInfo
